import * as React from "react";

import { FormField } from "@/components/ui/form-field";
import { Textarea } from "@/components/ui/textarea";
import { DangerConfirmDialog } from "@/routes/admin/DangerConfirmDialog";

// Story 11.8 AC9 — the Disable-access confirmation and its Reactivate mirror.
//
// The reversibility is stated OUT LOUD in the must-read Banner. An admin who
// believes disabling is permanent will escalate to support instead of using the
// control, and one who does not know sessions are revoked will assume the target
// stays signed in until their token expires. Both are said in the same sentence
// block, above the buttons, as a Banner rather than description prose.

/** 11.5's own limit — the field is optional and capped at 500 characters. */
export const DISABLE_REASON_MAX_LENGTH = 500;

export const DISABLE_ACCESS_MUST_READ =
  "Access ends immediately: active sessions and refresh tokens are revoked, and the user is signed out everywhere. Their history and audit trail are retained, and this can be undone with Reactivate.";

export interface DisableAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  isPending: boolean;
  /** Receives the trimmed reason, or undefined when the admin left it blank. */
  onConfirm: (reason?: string) => void;
}

export function DisableAccessDialog({
  open,
  onOpenChange,
  userName,
  isPending,
  onConfirm,
}: DisableAccessDialogProps) {
  const [reason, setReason] = React.useState("");

  // Reset between openings so a reason typed and then cancelled cannot ride along
  // with the NEXT disable — dismissal is a true no-op, including for this field.
  React.useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <DangerConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Disable access"
      description={`${userName} will lose access to AMPS immediately.`}
      mustRead={DISABLE_ACCESS_MUST_READ}
      confirmVariant="destructive"
      confirmLabel="Disable access"
      pendingLabel="Disabling…"
      isPending={isPending}
      onConfirm={() => onConfirm(reason.trim() || undefined)}
    >
      <FormField
        label="Reason (optional)"
        htmlFor="disable-access-reason"
        helper="Recorded on the audit trail. The user never sees it."
      >
        <Textarea
          id="disable-access-reason"
          value={reason}
          maxLength={DISABLE_REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
    </DangerConfirmDialog>
  );
}

export interface ReactivateAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  isPending: boolean;
  onConfirm: () => void;
}

export function ReactivateAccessDialog({
  open,
  onOpenChange,
  userName,
  isPending,
  onConfirm,
}: ReactivateAccessDialogProps) {
  return (
    <DangerConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Reactivate access"
      description={`${userName} will be able to sign in again.`}
      mustRead="They will need to sign in from scratch — the sessions revoked when access was disabled are not restored."
      mustReadVariant="info"
      // Restoring access is not destructive; `destructive` stays reserved for the
      // action that takes access away.
      confirmVariant="primary"
      confirmLabel="Reactivate access"
      pendingLabel="Reactivating…"
      isPending={isPending}
      onConfirm={onConfirm}
    />
  );
}
